
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import BaseCrudRepository from '@/repositories/BaseCrudRepository';
import { combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import FormModel from '@/repositories/data/FormModel';
import GlobalState from '@/util/GlobalState';
import OrderingFormModel from '@/repositories/data/OrderingFormModel';
import Resource from '@/repositories/Resource';

@Component
export default class OrderingForm extends Vue {
    private readonly relationship = 'order';

    @Inject()
    private readonly globalState!: GlobalState;

    @Prop({ type: Array, required: true })
    private readonly columns!: [string];

    @Prop({ type: String, required: true })
    private readonly i18nGroup!: string;

    @Prop({ type: Object, required: true })
    private readonly parentEntityRepository!: BaseCrudRepository<any, any, any>;

    @Prop({ type: Number, required: true })
    private readonly parentId!: number;

    @Prop({ type: Object, required: true })
    private readonly parentModel!: FormModel;

    private loading = true;
    private refreshKey = 0;
    private items: any[] = [];
    private formLoading = false;
    private orderingFormModel: OrderingFormModel | null = null;
    private formDialog = false;
    private deleteSubscription = new Subscription();
    private parentRepositorySubject = new ReplaySubject<BaseCrudRepository<any, any, any>>(1);
    private parentIdSubject = new ReplaySubject<number>(1);
    private relationshipSubject = new ReplaySubject<string>(1);
    private listingSubscription = new Subscription();
    private formModelSubscription = new Subscription();

    public created(): void {
        this.listingSubscription = combineLatest([
            this.parentRepositorySubject,
            this.parentIdSubject,
            this.relationshipSubject,
        ])
            .pipe(switchMap(([repository, id, relationship]) => repository.findRelated(id, relationship)))
            .subscribe((relatedResource) => {
                if (relatedResource.isSuccess) {
                    this.items = relatedResource.data ? relatedResource.data : [];
                }
                this.loading = relatedResource.isLoading;
                if (relatedResource.isError) {
                    this.$emit('error', relatedResource.error);
                }
            });

        this.relationshipSubject.next(this.relationship);
    }

    public beforeDestroy(): void {
        this.deleteSubscription.unsubscribe();
        this.listingSubscription.unsubscribe();
        this.formModelSubscription.unsubscribe();
    }

    private onNew(): void {
        this.formModelSubscription.unsubscribe();
        this.orderingFormModel = null;
        this.formDialog = true;
    }

    private cancelForm(): void {
        this.formModelSubscription.unsubscribe();
        this.orderingFormModel = null;
        this.formDialog = false;
    }

    private onSubmit(): void {
        this.setInputForm();
        if (this.orderingFormModel !== null) {
            this.formModelSubscription.unsubscribe();
            this.formModelSubscription = this.parentEntityRepository
                .putRelation(this.parentId, this.relationship, this.orderingFormModel)
                .subscribe((resource) => {
                    if (resource.isSuccess) {
                        this.orderingFormModel = null;
                        this.formDialog = false;
                        this.relationshipSubject.next(this.relationship);
                        this.extractOrder(resource);
                        this.globalState.toast(this.$t('crud.' + this.i18nGroup + '.ui.updated').toString());
                    } else if (resource.isError) {
                        this.$emit('error', resource.error);
                    }
                });
        }
    }

    private extractOrder(resource: Resource<any>) {
        this.parentModel.groups.forEach(function (g) {
            g.fields.forEach(function (f) {
                if (f.name === 'order') {
                    f.value = resource.data.order;
                    return;
                }
            });
        });
    }

    private setInputForm() {
        let idList: any[] = [];
        let orderList: number[] = [];
        for (let i = 0; i < this.items.length; i++) {
            idList[i] = this.items[i].id;
            orderList[i] = i;
        }
        this.orderingFormModel = new OrderingFormModel(idList, orderList);
    }

    private onError(error: any) {
        this.$emit('error', error);
    }

    @Watch('parentEntityRepository', { immediate: true })
    private onParentEntityRepositoryChanged(repository: BaseCrudRepository<any, any, any>): void {
        this.parentRepositorySubject.next(repository);
    }

    @Watch('parentId', { immediate: true })
    private onParentIdChanged(id: number): void {
        this.parentIdSubject.next(id);
    }

    private moveUp(item: object) {
        let currentIndex = this.items.indexOf(item);
        if (currentIndex === 0) {
            return;
        } else {
            [this.items[currentIndex], this.items[currentIndex - 1]] = [
                this.items[currentIndex - 1],
                this.items[currentIndex],
            ];
        }
        this.refreshKey++;
    }

    private moveDown(item: object) {
        let currentIndex = this.items.indexOf(item);
        if (currentIndex === this.items.length - 1) {
            return;
        } else {
            [this.items[currentIndex], this.items[currentIndex + 1]] = [
                this.items[currentIndex + 1],
                this.items[currentIndex],
            ];
        }
        this.refreshKey++;
    }
}
